import { ReactNode } from 'react';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { OpenerGroup } from '../../api/useGroups';
import { useGroupLiveSummary } from '../../api/useGroupLiveSummary';
import { useGroupLivePosition } from '../../context/PositionsProvider';
import { formatUsd, pnlColor } from '../../utils/format';

const ageLabel = (computedAt?: number): string => {
  if (computedAt == null) return '—';
  const secs = Math.max(0, Math.round((Date.now() - computedAt) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
};

const Metric = ({
  label,
  children,
  prominent = false,
  color,
  ...rest
}: {
  label: string;
  children: ReactNode;
  prominent?: boolean;
  color?: string;
  [key: `data-${string}`]: string | undefined;
}) => (
  <Stack spacing={0.25} {...rest}>
    <Typography
      sx={{ fontSize: '0.6rem', color: 'grey.500', letterSpacing: 0.2 }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: prominent ? '1.05rem' : '0.85rem',
        fontWeight: prominent ? 700 : 500,
        color: color ?? 'common.white',
        lineHeight: 1.2
      }}
    >
      {children}
    </Typography>
  </Stack>
);

/**
 * Live trading summary for ONE selected opener group. Two sources kept
 * separate: descalce + contract counts come from the socket
 * (`useGroupLivePosition`, real-time); nominal / PnL money comes from the REST
 * hook (`useGroupLiveSummary`). No client-side money math.
 */
/**
 * A figure whose `computedAt` is older than this is treated as no-longer-live
 * (the group stopped broadcasting, e.g. it finished, or the money endpoint
 * stopped refreshing) → shown as `—` rather than a frozen last value.
 */
const STALE_MS = 15_000;

export const GroupLiveSummaryStrip = ({ group }: { group: OpenerGroup }) => {
  const live = useGroupLivePosition(group._id);
  const { data: money } = useGroupLiveSummary(group._id);

  const now = Date.now();
  const liveFresh = !!live && now - live.computedAt <= STALE_MS;
  // Also require the money payload to belong to THIS group: react-query's
  // `keepPreviousData` serves the prior group's summary for one fetch latency
  // after a group switch — its `computedAt` is fresh, so without this guard the
  // previous group's Nominal/PnL would render under the new group's header.
  const moneyFresh =
    !!money &&
    money.groupId === group._id &&
    now - money.computedAt <= STALE_MS;

  const descalce = liveFresh ? live!.descalceTotal : undefined;
  const contracts = liveFresh ? live!.contractsBought : undefined;
  const contractsLabel = contracts
    ? `${contracts.kalshi} / ${contracts.poly} / ${contracts.total}`
    : '—';

  const pnl = moneyFresh ? money!.livePnlUsd : undefined;

  return (
    <Stack
      direction="row"
      spacing={3}
      alignItems="center"
      data-testid="group-live-summary"
      sx={{
        py: 0.75,
        px: 1.5,
        mb: 0.5,
        borderRadius: 1,
        backgroundColor: 'action.hover',
        flexShrink: 0,
        flexWrap: 'wrap'
      }}
    >
      <Metric label="Descalce" prominent color={descalce ? '#f44336' : undefined}>
        {descalce != null ? descalce.toLocaleString() : '—'}
      </Metric>

      <Metric label="Contratos (K / P / total)">{contractsLabel}</Metric>

      <Metric label="Nominal $ (con fees)">
        {moneyFresh ? formatUsd(money!.nominalUsd) : '—'}
      </Metric>

      <Metric
        label="PnL en vivo $"
        color={pnl != null ? pnlColor(pnl) : undefined}
        data-pnl-state={
          pnl == null ? 'unknown' : pnl < 0 ? 'negative' : 'positive'
        }
      >
        {moneyFresh ? formatUsd(money!.livePnlUsd) : '—'}
      </Metric>

      <Box sx={{ flex: 1 }} />

      {money && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>
            money {ageLabel(money.computedAt)}
          </Typography>
          {!moneyFresh && (
            <Tooltip title="The money figures are stale — the last live-summary refresh is behind.">
              <Chip
                label="stale"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ fontSize: '0.55rem', height: 16 }}
              />
            </Tooltip>
          )}
        </Stack>
      )}
    </Stack>
  );
};
